import lodashIsNumber from 'lodash-es/isNumber';
import type { ComputedRef, Ref } from 'vue';

import { type AuctionItemEntity, type AuctionListingItemEntity, AuctionState, BackendErrorCode } from '~/apiClient';
import type { SaleStatus } from '~/types/saleStatus';

interface UseAuctionStatusReturn extends SaleStatus {
    isCountdownVisible: ComputedRef<boolean>;
    activeAutoBidAmount: ComputedRef<number | undefined>;
}

type AuctionStatusColorScheme = 'green' | 'yellow' | 'blue' | 'neutral';

export default function useAuctionStatus(auction: Ref<AuctionItemEntity | AuctionListingItemEntity>): UseAuctionStatusReturn {
    const { tt } = useTypedI18n();
    const { data: authData } = useAuthUtils();
    const { extendNegotiation } = useExtendedNegotiation();
    const dayjs = useDayjs();

    const userMeta = computed(() => auction.value.userMeta ?? null);
    const extendedNegotiation = computed(() => auction.value.negotiation && extendNegotiation(auction.value.negotiation));

    // Indicates whether the auction or negotiation is awarded for buyer or seller users. Always false for third-party users or guests.
    const isAwardedForCurrentUser = computed(() => {
        if ('orderId' in auction.value) {
            return !!auction.value?.orderId;
        }

        if (!isSeller.value && (auction.value.userMeta?.userBids ?? 0) < 1) return false; // Neither seller nor buyer

        const wonByAuction =
            auction.value.state === AuctionState.Expired && (userMeta.value?.maxBidder || isSeller.value) && auction.value?.reservePriceReached;

        return wonByAuction || !!extendedNegotiation.value?.isSuccessful;
    });

    const isPriceHidden = computed(() => !lodashIsNumber(auction.value.currentPrice));

    const isFeeNoticeHidden = computed(
        () =>
            isPriceHidden.value ||
            !auction.value.state ||
            !(<AuctionState[]>[AuctionState.Waiting, AuctionState.Pending, AuctionState.Running, AuctionState.Expired]).includes(auction.value.state)
    );

    const isCountdownVisible = computed(() => auction.value.state === AuctionState.Pending || auction.value.state === AuctionState.Running);

    const colorScheme = computed<AuctionStatusColorScheme>(() => {
        if (auction.value.state === AuctionState.Running) {
            if (userMeta.value?.maxBidder) return 'green';
            if (userMeta.value?.outBid) return 'yellow';

            return 'blue';
        }

        if (isAwardedForCurrentUser.value) return 'green';

        if (extendedNegotiation.value?.isInProgress) return 'blue';

        return 'neutral';
    });

    const statusSectionColor = computed(() => {
        const colors: Record<AuctionStatusColorScheme, string> = {
            green: 'bg-success-50',
            yellow: 'bg-amber-50',
            blue: 'bg-primary-50',
            neutral: 'bg-neutral-100',
        } as const;

        return colors[colorScheme.value];
    });

    const title = computed(() => {
        switch (auction.value.state) {
            case AuctionState.Draft:
            case AuctionState.ToBeApproved:
            case AuctionState.ToBeScheduled:
                return tt('sdp.Auction.auctionStatus.sellerReservePrice');
            case AuctionState.Pending:
                return tt('sdp.Auction.auctionStatus.startingPrice');
            case AuctionState.Running:
                return tt('sdp.Auction.auctionStatus.current');
            case AuctionState.Canceled:
                return tt(`sdp.Auction.state.${AuctionState.Canceled}`);
            case AuctionState.Expired:
                return isAwardedForCurrentUser.value ? tt('sdp.Auction.auctionStatus.wonAuction') : tt(`sdp.Auction.state.${AuctionState.Expired}`);
        }
    });

    const titleColor = computed(() => {
        if (isAwardedForCurrentUser.value) return 'text-success-600';
    });

    const titleAnnotation = computed(() => {
        if (auction.value.state === AuctionState.Running) {
            if (userMeta.value?.maxBidder) return tt('sdp.Auction.auctionStatus.maxBidder');
            if (userMeta.value?.outBid) return tt('sdp.Auction.auctionStatus.outBid');
        }

        if (extendedNegotiation.value?.isInProgress) return tt('sdp.Auction.auctionStatus.negotiationInProgress');
    });

    const titleAnnotationColor = computed(() => {
        const colors: Partial<Record<AuctionStatusColorScheme, string>> = {
            green: 'text-success-600',
            yellow: 'text-warning-600',
            blue: 'text-primary-600',
        } as const;

        return colors[colorScheme.value];
    });

    const priceColor = computed(() => {
        if (isPriceHidden.value) return 'text-default';

        if (auction.value.state === AuctionState.Expired) return isAwardedForCurrentUser.value ? 'text-success-800' : 'text-default';

        return 'text-primary';
    });

    const isBailNeeded = computed(() => !userMeta.value?.canBid && userMeta.value?.cantBidReason === BackendErrorCode.CantPerformUserNoBail);

    const hasRestrictedNotice = computed(
        () =>
            !!userMeta.value &&
            !userMeta.value?.canBid &&
            userMeta.value?.cantBidReason !== BackendErrorCode.CantBidAuctionIsNotRunning &&
            !isBailNeeded.value
    );

    const hasInspectionNotice = computed(() => !!userMeta.value?.inspectionExpiredExportRestrictions);

    const activeAutoBidAmount = computed<number | undefined>(() => (userMeta.value?.autoBid?.active ? userMeta.value?.autoBid?.amount : undefined));

    const isSeller = computed(() => authData.value?.user?.companyId === auction.value?.sellerCompanyId);

    const inspectionReminder = computed(() => {
        if (!isSeller.value) return;

        const now = dayjs();
        const target = dayjs(auction.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        isFeeNoticeHidden,
        isCountdownVisible,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        activeAutoBidAmount,
        isSeller,
        inspectionReminder,
    };
}
